// src/pages/fleet/reports/ReportPages.tsx — Reports submenu: one page per report.
// Each page = filters (date range · vehicle · report-specific) → ReportTable with
// Excel / CSV / PDF export. Column sets live in ./columns.
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getAlertReport, getDeviceHealth, getOverspeedReport, getStoppageReport, getTripPerformance, getTripReport, getVehicleSummary,
} from '../../../services/fleetApi';
import type { TripPerformanceSummary } from '../../../services/fleetApi';
import {
  FilterSelect, RangePicker, ReportShell, ReportTable, VehicleSelect, fmtDT, fmtDur, fmtKm, humanize,
  presetRange, useReport, Range,
} from './reportKit';
import { ALERT_COLS, DEVICE_COLS, OVERSPEED_COLS, PERF_COLS, STOPPAGE_COLS, TRIP_COLS, VEHICLE_COLS, fmtExtra } from './columns';

const rangeText = (r: Range) => `${fmtDT(r.from)} – ${fmtDT(r.to)}`;
const sum = <T,>(rows: T[], f: (r: T) => number) => rows.reduce((n, r) => n + (f(r) || 0), 0);
const MOVEMENT_NOTE = 'Built from GPS points recorded while a truck is on a trip.';

// Shared filter state for the date-range reports.
function useFilters() {
  const [range, setRange] = useState<Range>(() => presetRange('7d'));
  const [vehicleId, setVehicleId] = useState('');
  return { range, setRange, vehicleId, setVehicleId, params: { from: range.from, to: range.to, vehicleId: vehicleId || undefined } };
}

// ── Trip Report ───────────────────────────────────────────────────────────────
export function TripReportPage() {
  const navigate = useNavigate();
  const f = useFilters();
  const [status, setStatus] = useState('');
  const { rows, loading, error } = useReport(
    () => getTripReport({ ...f.params, status: status || undefined }).then((r) => r.data.rows),
    [f.range, f.vehicleId, status],
  );
  return (
    <ReportShell icon="list" tint="#FF9500" title="Trip Report" subtitle="Every trip in the period with timings, distance and consignment details"
      filters={<>
        <RangePicker value={f.range} onChange={f.setRange} />
        <VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} />
        <FilterSelect label="Status" value={status} onChange={setStatus}
          options={['PENDING', 'ACTIVE', 'COMPLETED', 'CANCELLED'].map((s) => ({ value: s, label: humanize(s) }))} />
      </>}
      stats={[
        { label: 'Trips', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Completed', value: rows.filter((r) => r.status === 'COMPLETED').length, tint: 'var(--fx-green)' },
        { label: 'Active', value: rows.filter((r) => r.status === 'ACTIVE').length, tint: 'var(--fx-orange)' },
        { label: 'Distance Travelled', value: fmtKm(Math.round(sum(rows, (r) => r.distanceKm) * 10) / 10), tint: 'var(--fx-purple)' },
        { label: 'Quantity (MT)', value: sum(rows, (r) => r.quantityMt || 0).toLocaleString('en-IN'), tint: 'var(--fx-text-3)' },
      ]}>
      <ReportTable title="Trip Report" subtitle={rangeText(f.range)} columns={TRIP_COLS} rows={rows} loading={loading} error={error}
        onRowClick={(r) => navigate(`/fleet/trips/${r.id}`)} />
    </ReportShell>
  );
}

// ── Trip Performance (route deviation + time vs estimate) ────────────────────
export function TripPerformancePage() {
  const navigate = useNavigate();
  const f = useFilters();
  const [show, setShow] = useState('');
  const [summary, setSummary] = useState<TripPerformanceSummary | null>(null);
  const { rows, loading, error } = useReport(
    () => getTripPerformance(f.params).then((r) => { setSummary(r.data.summary); return r.data.rows; }),
    [f.range, f.vehicleId],
  );
  const shown = useMemo(() => rows.filter((r) =>
    show === 'deviated' ? r.deviations > 0
      : show === 'late' ? ['LATE', 'RUNNING_LATE', 'NOT_STARTED_LATE'].includes(r.verdict)
        : show === 'ontime' ? r.verdict === 'ON_TIME' || r.verdict === 'EARLY'
          : true), [rows, show]);
  return (
    <ReportShell icon="clock" tint="#34C759" title="Trip Performance" subtitle="How often trucks left the route, and how long trips took against the estimate"
      filters={<>
        <RangePicker value={f.range} onChange={f.setRange} />
        <VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} />
        <FilterSelect label="Show" value={show} onChange={setShow} options={[
          { value: 'deviated', label: 'Went off route' }, { value: 'late', label: 'Late' }, { value: 'ontime', label: 'On time' },
        ]} />
      </>}
      note={summary ? `Off route = more than ${summary.deviationThresholdKm} km from the planned route. On time = arrived within ±${summary.graceMin} min of the estimated end.` : undefined}
      stats={[
        { label: 'Times Off Route', value: summary ? summary.deviationEvents : '—', tint: 'var(--fx-red)' },
        { label: 'Trips Off Route', value: summary ? `${summary.deviationTrips} / ${summary.trips}` : '—', tint: 'var(--fx-orange)' },
        { label: 'On-Time Rate', value: summary?.onTimePct == null ? '—' : `${summary.onTimePct}%`, tint: 'var(--fx-green)' },
        { label: 'Late Trips', value: summary ? summary.late : '—', tint: 'var(--fx-red)' },
        { label: 'Avg. Extra Time', value: summary ? fmtExtra(summary.avgExtraMin) : '—', tint: 'var(--fx-purple)' },
      ]}>
      <ReportTable title="Trip Performance" subtitle={rangeText(f.range)} columns={PERF_COLS} rows={shown} loading={loading} error={error}
        onRowClick={(r) => navigate(`/fleet/trips/${r.id}`)} />
    </ReportShell>
  );
}

// ── Vehicle Summary (distance / running / halt) ───────────────────────────────
export function VehicleSummaryPage() {
  const f = useFilters();
  const { rows, loading, error } = useReport(() => getVehicleSummary(f.params).then((r) => r.data.rows), [f.range, f.vehicleId]);
  return (
    <ReportShell icon="truck" tint="#007AFF" title="Vehicle Summary" subtitle="Distance, running time, halt time and speed for each truck"
      note={MOVEMENT_NOTE}
      filters={<><RangePicker value={f.range} onChange={f.setRange} /><VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} /></>}
      stats={[
        { label: 'Vehicles', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Total Distance', value: fmtKm(Math.round(sum(rows, (r) => r.distanceKm) * 10) / 10), tint: 'var(--fx-purple)' },
        { label: 'Running Time', value: fmtDur(sum(rows, (r) => r.runningMin)), tint: 'var(--fx-green)' },
        { label: 'Halt Time', value: fmtDur(sum(rows, (r) => r.haltMin)), tint: 'var(--fx-orange)' },
      ]}>
      <ReportTable title="Vehicle Summary" subtitle={rangeText(f.range)} columns={VEHICLE_COLS} rows={rows} loading={loading} error={error}
        initialSort={{ key: 'distanceKm', dir: -1 }} />
    </ReportShell>
  );
}

// ── Stoppage Report ───────────────────────────────────────────────────────────
export function StoppageReportPage() {
  const navigate = useNavigate();
  const f = useFilters();
  const [minMinutes, setMinMinutes] = useState('15');
  const { rows, loading, error } = useReport(
    () => getStoppageReport({ ...f.params, minMinutes }).then((r) => r.data.rows),
    [f.range, f.vehicleId, minMinutes],
  );
  const longest = rows.reduce((m, r) => Math.max(m, r.durationMin), 0);
  return (
    <ReportShell icon="pin" tint="#FF3B30" title="Stoppage Report" subtitle="Where trucks stopped during trips, and for how long"
      note={MOVEMENT_NOTE}
      filters={<>
        <RangePicker value={f.range} onChange={f.setRange} />
        <VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} />
        <select style={sel} value={minMinutes} onChange={(e) => setMinMinutes(e.target.value)} aria-label="Minimum stop">
          {['5', '10', '15', '30', '60', '120'].map((m) => <option key={m} value={m}>Stops ≥ {fmtDur(+m)}</option>)}
        </select>
      </>}
      stats={[
        { label: 'Stops', value: rows.length, tint: 'var(--fx-red)' },
        { label: 'Total Halt', value: fmtDur(sum(rows, (r) => r.durationMin)), tint: 'var(--fx-orange)' },
        { label: 'Longest Stop', value: fmtDur(longest), tint: 'var(--fx-purple)' },
        { label: 'Vehicles', value: new Set(rows.map((r) => r.vehicleNumber)).size, tint: 'var(--fx-accent)' },
      ]}>
      <ReportTable title="Stoppage Report" subtitle={`${rangeText(f.range)} · stops of ${fmtDur(+minMinutes)} or more`} columns={STOPPAGE_COLS}
        rows={rows} loading={loading} error={error} onRowClick={(r) => navigate(`/fleet/trips/${r.tripId}`)} />
    </ReportShell>
  );
}

// ── Overspeed Report ──────────────────────────────────────────────────────────
export function OverspeedReportPage() {
  const navigate = useNavigate();
  const f = useFilters();
  const [limit, setLimit] = useState('80');
  const { rows, loading, error } = useReport(
    () => getOverspeedReport({ ...f.params, limit }).then((r) => r.data.rows),
    [f.range, f.vehicleId, limit],
  );
  return (
    <ReportShell icon="chart" tint="#AF52DE" title="Overspeed Report" subtitle="Every stretch where a truck drove above the speed limit"
      note={MOVEMENT_NOTE}
      filters={<>
        <RangePicker value={f.range} onChange={f.setRange} />
        <VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} />
        <select style={sel} value={limit} onChange={(e) => setLimit(e.target.value)} aria-label="Speed limit">
          {['50', '60', '70', '80', '90', '100'].map((m) => <option key={m} value={m}>Above {m} km/h</option>)}
        </select>
      </>}
      stats={[
        { label: 'Overspeed Events', value: rows.length, tint: 'var(--fx-red)' },
        { label: 'Vehicles', value: new Set(rows.map((r) => r.vehicleNumber)).size, tint: 'var(--fx-accent)' },
        { label: 'Top Speed', value: rows.length ? `${Math.max(...rows.map((r) => r.maxSpeed))} km/h` : '—', tint: 'var(--fx-purple)' },
        { label: 'Time Overspeeding', value: fmtDur(sum(rows, (r) => r.durationMin)), tint: 'var(--fx-orange)' },
      ]}>
      <ReportTable title="Overspeed Report" subtitle={`${rangeText(f.range)} · limit ${limit} km/h`} columns={OVERSPEED_COLS}
        rows={rows} loading={loading} error={error} onRowClick={(r) => navigate(`/fleet/trips/${r.tripId}`)} />
    </ReportShell>
  );
}

// ── Alert Report ──────────────────────────────────────────────────────────────
export function AlertReportPage() {
  const navigate = useNavigate();
  const f = useFilters();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const { rows, loading, error } = useReport(
    () => getAlertReport({ ...f.params, type: type || undefined, status: status || undefined })
      .then((r) => { setTypes(r.data.types || []); return r.data.rows; }),
    [f.range, f.vehicleId, type, status],
  );
  const shown = useMemo(() => (severity ? rows.filter((r) => r.severity === severity) : rows), [rows, severity]);
  return (
    <ReportShell icon="bell" tint="#FF3B30" title="Alert Report" subtitle="GPS, route and device alerts raised in the period"
      filters={<>
        <RangePicker value={f.range} onChange={f.setRange} />
        <VehicleSelect value={f.vehicleId} onChange={f.setVehicleId} />
        <FilterSelect label="Type" value={type} onChange={setType} options={types.map((t) => ({ value: t, label: humanize(t) }))} />
        <FilterSelect label="Severity" value={severity} onChange={setSeverity}
          options={['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((s) => ({ value: s, label: humanize(s) }))} />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={[{ value: 'open', label: 'Open' }, { value: 'resolved', label: 'Resolved' }]} />
      </>}
      stats={[
        { label: 'Alerts', value: shown.length, tint: 'var(--fx-accent)' },
        { label: 'Open', value: shown.filter((r) => r.status === 'Open').length, tint: 'var(--fx-red)' },
        { label: 'High / Critical', value: shown.filter((r) => r.severity === 'HIGH' || r.severity === 'CRITICAL').length, tint: 'var(--fx-orange)' },
        { label: 'Vehicles', value: new Set(shown.map((r) => r.vehicleNumber)).size, tint: 'var(--fx-purple)' },
      ]}>
      <ReportTable title="Alert Report" subtitle={rangeText(f.range)} columns={ALERT_COLS} rows={shown} loading={loading} error={error}
        onRowClick={(r) => r.tripId && navigate(`/fleet/trips/${r.tripId}`)} />
    </ReportShell>
  );
}

// ── Device Health Report (current snapshot, no date range) ────────────────────
const HEALTH = ['MOVING', 'HALTED', 'DELAYED', 'GPS_GAP', 'OFFLINE'];
export function DeviceHealthPage() {
  const [params, setParams] = useSearchParams();
  const health = params.get('health') || '';
  const setHealth = (h: string) => setParams(h ? { health: h } : {}, { replace: true });
  const [tick, setTick] = useState(0);
  const { rows, loading, error } = useReport(() => getDeviceHealth().then((r) => r.data.rows), [tick]);
  const shown = useMemo(() => (health ? rows.filter((r) => r.healthStatus === health) : rows), [rows, health]);
  const n = (h: string) => rows.filter((r) => r.healthStatus === h).length;
  return (
    <ReportShell icon="antenna" tint="#30B0C7" title="Device Health" subtitle="Live condition of every truck's GPS device"
      filters={<>
        <FilterSelect label="Health" value={health} onChange={setHealth} options={HEALTH.map((h) => ({ value: h, label: humanize(h) }))} />
        <button type="button" style={refresh} onClick={() => setTick((t) => t + 1)}>⟳ Refresh</button>
        <span style={{ fontSize: 12, color: 'var(--fx-text-3)' }}>As of {fmtDT(new Date().toISOString())}</span>
      </>}
      stats={[
        { label: 'Devices', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Moving', value: n('MOVING'), tint: 'var(--fx-green)' },
        { label: 'Halted', value: n('HALTED'), tint: 'var(--fx-orange)' },
        { label: 'Delayed / GPS Gap', value: n('DELAYED') + n('GPS_GAP'), tint: 'var(--fx-purple)' },
        { label: 'Offline', value: n('OFFLINE'), tint: 'var(--fx-text-3)' },
      ]}>
      <ReportTable title="Device Health Report" subtitle={`As of ${fmtDT(new Date().toISOString())}`} columns={DEVICE_COLS}
        rows={shown} loading={loading} error={error} initialSort={{ key: 'gpsAgeMinutes', dir: -1 }} />
    </ReportShell>
  );
}

const sel: React.CSSProperties = {
  padding: '6px 9px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', fontSize: 12,
  background: 'var(--fx-input)', color: 'var(--fx-text)', fontFamily: 'inherit',
};
const refresh: React.CSSProperties = { ...sel, cursor: 'pointer', fontWeight: 600 };
